import {
  TableRow, TableCell, Grid, Typography, TextField, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, Snackbar, Box, Tooltip, useMediaQuery, IconButton, useTheme,
} from '@mui/material';
import { AddOrderItemRequest, Item, client } from '../../../../app/services/api/orderManagementClient';
import { ChangeEvent, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyRoundAndFormat } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import { UserContext } from '../../../../components/shared/useUser';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../../enums/OrderCategories';
import TextWithOverflowTooltip from '../../../../components/shared/TextWithOverflowTooltip';
import { useNavigate } from 'react-router-dom';
import { PredictedDemandModel } from '../../../../app/services/api/podClient';
import { MagicWand01 } from '../../../../assets';
import { QuantityWarningDialog } from '../../../../components/shared/orders/QuantityWarningDialog';
import NoProductImage from '../../../../assets/images/NoProductImage.png';
import { debounce } from 'lodash';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';

type ProductRowProps = {
  product: Item;
  selectedOrderId?: number;
  basketQuantity?: number;
  onAddItemConfirmed: () => void;
  currencyCode?: string;
  showMessageWhenNoCost: boolean;
  predictedItem?: PredictedDemandModel;
  showPredictedQuantity?: boolean;
  canEditPredictions?: boolean;
  orderType: number;
  orderItemId?: number;
}

const ProductRow = forwardRef<{ matchPredictedQuantities: () => void } | null, ProductRowProps>(({ product, selectedOrderId, basketQuantity, currencyCode, onAddItemConfirmed, showMessageWhenNoCost, orderType, predictedItem, showPredictedQuantity, canEditPredictions = true, orderItemId }, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation('products');
  const theme = useTheme();
  const { logError } = useLogError();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const [quantity, setQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [startingQuantity, setStartingQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [, setLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const predictedDemandItem = predictedItem?.predictedDemandItems?.at(0);
  const predictedQuantity = predictedDemandItem?.quantity.toString();
  const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
  const borderColor = showPrediction && predictedQuantity === quantity ? theme.palette.success[400] : '';
  const safetyStock = predictedDemandItem?.safetyStock;
  const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    setQuantity(basketQuantity?.toString() ?? '0');
    setStartingQuantity(basketQuantity?.toString() ?? '0');
  }, [basketQuantity]);

  const onQuantityChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault();
    const value = Math.abs(Number(event.target.value || '0'));
    setQuantity(value.toString());
  };

  const handleMatchQuantityChange = () => {
    setQuantity(predictedQuantity?.toString() ?? '');
  };

  useImperativeHandle(ref, () => ({
    matchPredictedQuantities,
  }));

  const matchPredictedQuantities = () => {
    if (showPrediction) {
      setQuantity(predictedQuantity ?? quantity);
    }
  };

  const debouncedAddItemToOrder = useCallback(
    debounce((itemId: number, quantity: number) => {
      onAddItemToOrder(itemId, quantity);
    }, 500),
    [],
  );

  const onAddItemToOrder = async (itemId: number, quantity: number) => {
    try {
      setLoading(true);
      const request: AddOrderItemRequest = {
        orderId: selectedOrderId !== undefined ? selectedOrderId : 0,
        itemId,
        quantity,
        predictedQuantity: quantity,
      };
      const response = await client.addOrderItem(selectedStore?.storeNumber, request);
      if (response.success) {
        handleSnackbarOpen(t('addItemSuccessMessage'), 'success');
        setQuantity(quantity.toString());
        setStartingQuantity(quantity.toString());
        onAddItemConfirmed();
      }
      else {
        setWarningMessage(response.message);
        setShowQuantityWarningDialog(true);
        //fixQuantity();
      }
    } catch (error) {
      handleSnackbarOpen(t('addItemErrorMessage') + ' ' + error, 'error');
      logError(error);
    } finally {
      setLoading(false);
      setShowDialog(false);
    }
  };

  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    setLoading(true);
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteSuccess'), 'success');
        onAddItemConfirmed();
      } else {
        handleSnackbarOpen(t('deleteItemFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteItemFail') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hideDialogAndResetQuantity = () => {
    setShowDialog(false);
    setQuantity(startingQuantity);
  };

  const quantityError = (): string | null => {
    if (Number(quantity) == 0) {
      return null;
    }

    if ((product.maxQuantity != null) && Number(quantity) > product.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${product.maxQuantity}`;

    if ((product.minQuantity != null) && Number(quantity) < product.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${product.minQuantity}`;

    return null;
  };

  return (
    <>
      <TableRow key={product.id}>
        <TableCell sx={{ width: '80%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Grid container
            alignItems={'center'}
            spacing={1}
            onClick={hasPermissionTo(['ProductDetailView']) ? () => navigate(`/ofd/products/${product.sku}`, {
              state: {
                orderId: selectedOrderId,
                fromOrderPage: selectedOrderId ? true : false,
                canChangeOrderQuantity: true,
                quantity: basketQuantity || 0,
                orderItemId: orderItemId,
                fromOrderableItems: true,
              },
            }) : undefined}
            wrap="nowrap">
            <Grid item
              xs="auto">
              <Box
                style={{
                  width: '100px',
                  height: isLargeScreen ? '75px' : '100px',
                  justifyContent: 'center',
                  backgroundColor: (product?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #ddd',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  imageRendering: '-webkit-optimize-contrast',
                  backgroundImage: `url(${product.thumbnailUrl && product.thumbnailUrl.trim() !== '' ? product.thumbnailUrl : NoProductImage})`,

                }}
              />
            </Grid>
            <Grid item
              sx={{ minWidth: 0, paddingLeft: '10px !important', flex: 1 }}>
              {isLargeScreen ? (
                <Grid container
                  spacing={2}>
                  <Grid item
                    xs={6}>
                    <TextWithOverflowTooltip text={product.name} />
                    <TextWithOverflowTooltip text={product.sku} />
                    <TextWithOverflowTooltip text={isOrderCategory(product.category)
                      ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                      : product.category} />
                    {(showMessageWhenNoCost || product.price === 0) ?
                      <TextWithOverflowTooltip text='' />
                      :
                      <TextWithOverflowTooltip text={applyRoundAndFormat(product.price, currencyCode)} />
                    }
                  </Grid>
                  <Grid item
                    xs={6}>
                    <TextWithOverflowTooltip text={`${t('orderableUom')}: ${product.orderableUom} (${product.packSize}${product.primaryUom})`} />
                    <TextWithOverflowTooltip text={`${t('primaryUom')}: ${product.primaryUom}`} />
                    <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
                      arrow>
                      <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                        {t('soh')} {product.stockOnHand}
                      </Box>
                    </Tooltip>
                    <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
                      arrow>
                      <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                        {t('soo')}: {product.stockOnOrder}
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
              ) : (
                <>
                  {product.name}  <br />
                  {product.sku}   <br />
                  {isOrderCategory(product.category)
                    ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                    : product.category}   <br />
                  {(showMessageWhenNoCost || product.price === 0) ?
                    ''
                    :
                    applyRoundAndFormat(product.price, currencyCode)
                  } <br />
                  {t('orderableUom')}: {product.orderableUom} ({product.packSize}{product.primaryUom})<br />
                  {t('primaryUom')}: {product.primaryUom}  <br />
                  <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
                    arrow>
                    <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                      {t('soh')} {product.stockOnHand}
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
                    arrow>
                    <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                      {t('soo')}: {product.stockOnOrder}
                    </Box>
                  </Tooltip>
                </>
              )}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell>
          <TextField
            label={showPrediction ? t('predictedQuantity', { value: predictedQuantity }) : null}
            error={(quantityError()?.length ?? 0) > 0}
            helperText={quantityError()}
            size='small'
            type='number'
            inputProps={{ min: 0 }}
            autoComplete='off'
            disabled={showPrediction && !canEditPredictions}
            sx={{
              maxWidth: 100,
              width: '100%',
              input: {
                textAlign: 'center',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: borderColor,
                },
              },

              '& .MuiInputLabel-root': {
                color: borderColor,
              },
            }}
            value={quantity}
            onChange={e => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setQuantity(value);
              }
            }}
            onFocus={e => e.target.select()}
            onBlur={async e => {
              let newQuantity = Number(e.target.value || '0');
              if (isNaN(newQuantity) || newQuantity < 0) newQuantity = 0;
              if (newQuantity.toString() !== startingQuantity) {
                if (newQuantity === 0 && selectedOrderId && orderItemId) {
                  await handleDeleteOrderItem(selectedOrderId, orderItemId);
                } else {
                  debouncedAddItemToOrder(product.id, newQuantity);
                  setStartingQuantity(newQuantity.toString());
                }
              }
            }}
          ></TextField>
          {
            showPrediction &&
            <Tooltip title={
              <span>
                {
                  predictedDemandQuantity != null && (
                    <>
                      {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: product.primaryUom })}
                      <br />
                    </>
                  )
                }
                {safetyStock != null && t('safetyStock', { value: safetyStock, uom: product.primaryUom })}
              </span>
            }>
              <IconButton aria-label={t('applyPredictedQuantity')}
                disabled={!canEditPredictions}
                onClick={() => handleMatchQuantityChange()}>
                <MagicWand01
                  color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
              </IconButton>
            </Tooltip>
          }
        </TableCell>
      </TableRow>
      <Dialog open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('confirmDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>{t('confirmDialogContent', { item: product.name })}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions >
          <Button
            variant="secondary"
            onClick={hideDialogAndResetQuantity}>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={() => onAddItemToOrder(product.id, Number(quantity))}>
            {t('update')}
          </Button>
        </DialogActions>
      </Dialog>
      <QuantityWarningDialog setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
    </>
  );
});

ProductRow.displayName = 'ProductRow';

export default ProductRow;